import { useCallback, useEffect, useState } from "react";
import { useData } from "@/context/DataContext";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";
import { Empty } from "@/components/Common";
import { Badge } from "@/components/warehouse/PurchaseOrders";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export const RETURN_REASONS = { GUASTO: "Guasto", RECUPERATO: "Recuperato (cessazione)", SOSTITUITO: "Sostituito (upgrade)" };
export const RETURN_STATUS = { RETURNED_VAN: ["Nel furgone", "bg-amber-100 text-amber-900"], RETURN_RECEIVED: ["Da valutare", "bg-sky-100 text-sky-900"],
  REFURBISHING: ["In ricondizionamento", "bg-violet-100 text-violet-900"], IN_STOCK: ["Rimesso a stock", "bg-emerald-100 text-emerald-900"],
  SCRAPPED: ["Rottamato", "bg-zinc-200 text-zinc-700"], RETURNED_SUPPLIER: ["Reso al fornitore", "bg-zinc-100 text-zinc-700"] };

const Actions = ({ r, centrals, onDone }) => {
  const [wh, setWh] = useState("");
  const [notes, setNotes] = useState("");
  const call = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const decide = (decision, msg) => call(() => api.post(`/warehouse/returns/${r.id}/decide`, { decision, notes }), msg);
  if (r.status === "RETURNED_VAN") {
    const options = centrals.filter((c) => c.company_id === r.company_id || !r.company_id);
    return (
      <div className="flex items-center gap-2">
        <Select value={wh || undefined} onValueChange={setWh}>
          <SelectTrigger className="h-8 w-52" data-testid={`return-wh-${r.serial}`}><SelectValue placeholder="Magazzino di arrivo" /></SelectTrigger>
          <SelectContent>{(options.length ? options : centrals).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Button size="sm" disabled={!wh} onClick={() => call(() => api.post(`/warehouse/returns/${r.id}/receive`, { warehouse_id: wh }), "Reso ricevuto in magazzino")} data-testid={`return-receive-${r.serial}`}>Ricevi</Button>
      </div>
    );
  }
  const refurb = r.status === "REFURBISHING";
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Input className="h-8 w-40" placeholder="Note (facoltative)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`return-notes-${r.serial}`} />
      <Button size="sm" variant="outline" onClick={() => decide("RESTOCK", "Rimesso a stock")} data-testid={`return-restock-${r.serial}`}>{refurb ? "Ricondizionato: a stock" : "Rimetti a stock"}</Button>
      {!refurb && <Button size="sm" variant="outline" onClick={() => decide("REFURBISH", "Inviato a ricondizionamento")} data-testid={`return-refurbish-${r.serial}`}>Ricondiziona</Button>}
      {!refurb && <Button size="sm" variant="outline" onClick={() => decide("SUPPLIER", "Reso al fornitore")} data-testid={`return-supplier-${r.serial}`}>Rendi al fornitore</Button>}
      <Button size="sm" variant="ghost" className="text-red-700" onClick={() => decide("SCRAP", "Apparato rottamato")} data-testid={`return-scrap-${r.serial}`}>Rottama</Button>
    </div>
  );
};

export const ReturnsTab = ({ warehouses, onChanged }) => {
  const { can } = useAuth();
  const [status, setStatus] = useState("open");
  const { companyId } = useData();
  const [raw, setRows] = useState([]);
  const rows = companyId ? raw.filter((r) => warehouses.some((w) => w.id === r.warehouse_id || w.id === r.last_warehouse_id)) : raw;
  const load = useCallback(() => api.get("/warehouse/returns", { params: { status: status === "open" ? undefined : status } }).then((r) => setRows(r.data)).catch(() => {}), [status]);
  useEffect(() => { load(); }, [load]);
  const whs = Object.fromEntries(warehouses.map((w) => [w.id, w]));
  const centrals = warehouses.filter((w) => w.kind === "CENTRAL");
  const changed = () => { load(); onChanged(); };
  return (
    <div data-testid="returns-tab">
      <div className="mb-3 flex flex-wrap gap-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-60 bg-white" data-testid="returns-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Da gestire</SelectItem>
            {Object.entries(RETURN_STATUS).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {rows.length === 0 ? <Empty text="Nessun apparato in rientro" testId="returns-empty" /> : (
        <div className="overflow-x-auto border bg-white">
          <Table>
            <TableHeader><TableRow className="bg-muted/50"><TableHead>Seriale</TableHead><TableHead>Articolo</TableHead><TableHead>Motivo</TableHead><TableHead>Da ordine</TableHead><TableHead>Stato / posizione</TableHead><TableHead>Azioni</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`return-row-${r.serial}`}>
                  <TableCell className="font-mono text-sm font-semibold">{r.serial}</TableCell>
                  <TableCell className="text-sm"><span className="font-mono text-xs">{r.item_code}</span> {r.item_name}</TableCell>
                  <TableCell className="text-sm">{RETURN_REASONS[r.return_reason]}{r.return_notes && <div className="text-xs text-muted-foreground">{r.return_notes}</div>}</TableCell>
                  <TableCell className="text-xs">{r.return_order_id ? <Link className="font-mono font-semibold underline" to={`/ordini/${r.return_order_id}`}>{r.return_external_order_id}</Link> : "—"}<div className="text-muted-foreground">{r.returned_by} · {fmtDateTime(r.returned_at)}</div></TableCell>
                  <TableCell><Badge map={RETURN_STATUS} value={r.status} testId={`return-status-${r.serial}`} /><div className="mt-1 text-xs">{whs[r.warehouse_id || r.last_warehouse_id]?.name}</div>{r.decision && <div className="text-xs text-muted-foreground">{r.decision.label} · {r.decision.by}</div>}</TableCell>
                  <TableCell>{can("inventory:write") && ["RETURNED_VAN", "RETURN_RECEIVED", "REFURBISHING"].includes(r.status) && <Actions r={{ ...r, company_id: whs[r.warehouse_id]?.company_id }} centrals={centrals} onDone={changed} />}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
